import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, ClipboardList, Gauge, Layers3, LayoutGrid, Loader2, ShieldCheck } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { operationalExecutionAPI } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { OperationalWorkerTab } from '@/components/operational-execution/OperationalWorkerTab';
import { OperationalAdminTab } from '@/components/operational-execution/OperationalAdminTab';
import { OperationalDashboardTab } from '@/components/operational-execution/OperationalDashboardTab';
import { OperationalOverviewTab } from '@/components/operational-execution/OperationalOverviewTab';
import { toast } from 'sonner';

export default function OperationalExecutionPage() {
  const { hasPermission, hasRole } = useAuth();
  const canView = hasPermission('operational_execution.view') || hasPermission('operational_execution.manage') || hasRole(['admin', 'store_manager', 'supervisor', 'accountant', 'staff', 'sales', 'cashier', 'receptionist', 'technician', 'tech_lead', 'warehouse', 'procurement', 'customer_support', 'driver', 'marketing', 'intern']);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [referenceDate, setReferenceDate] = useState('');
  const [moduleData, setModuleData] = useState(null);

  const loadBootstrap = useCallback(async (dateOverride) => {
    const targetDate = dateOverride || referenceDate || undefined;
    try {
      setRefreshing(true);
      const response = await operationalExecutionAPI.getBootstrap(targetDate ? { reference_date: targetDate } : {});
      const payload = response?.data || response;
      setModuleData(payload);
      setReferenceDate(payload?.reference_date || targetDate || '');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível carregar Execução Operacional.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [referenceDate]);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    loadBootstrap();
  }, [canView, loadBootstrap]);

  const managerMode = Boolean(moduleData?.permissions?.is_manager);
  const activeTabs = useMemo(() => (
    managerMode ? ['overview', 'workspace', 'admin', 'dashboard'] : ['overview', 'workspace']
  ), [managerMode]);

  const handleComplete = async (activityId, payload) => {
    await operationalExecutionAPI.completeActivity(activityId, payload);
    toast.success('Actividade concluída.');
    await loadBootstrap(referenceDate);
  };

  const handleJustify = async (activityId, payload) => {
    await operationalExecutionAPI.justifyActivity(activityId, payload);
    toast.success('Justificação registada.');
    await loadBootstrap(referenceDate);
  };

  const handleComment = async (activityId, payload) => {
    await operationalExecutionAPI.commentActivity(activityId, payload);
    toast.success('Comentário adicionado.');
    await loadBootstrap(referenceDate);
  };

  const handleRequestCorrection = async (activityId, payload) => {
    await operationalExecutionAPI.requestCorrection(activityId, payload);
    toast.success('Pedido de correcção enviado para aprovação.');
    await loadBootstrap(referenceDate);
  };

  const handleGenerate = async (payload) => {
    await operationalExecutionAPI.generateActivities({ ...payload, reference_date: referenceDate || undefined });
    toast.success('Geração operacional concluída.');
    await loadBootstrap(referenceDate);
  };

  const handleSaveCatalog = async (catalogId, payload) => {
    if (catalogId) {
      await operationalExecutionAPI.updateCatalogActivity(catalogId, payload);
      toast.success('Catálogo actualizado.');
    } else {
      await operationalExecutionAPI.createCatalogActivity(payload);
      toast.success('Item de catálogo criado.');
    }
    await loadBootstrap(referenceDate);
  };

  const handleSetCatalogStatus = async (catalogId, isActive) => {
    await operationalExecutionAPI.setCatalogActivityStatus(catalogId, { is_active: isActive });
    toast.success(isActive ? 'Actividade activada.' : 'Actividade desactivada.');
    await loadBootstrap(referenceDate);
  };

  const handleDeleteCatalog = async (catalogId) => {
    await operationalExecutionAPI.deleteCatalogActivity(catalogId);
    toast.success('Actividade removida do catálogo.');
    await loadBootstrap(referenceDate);
  };

  const handleSaveReason = async (reasonId, payload) => {
    if (reasonId) {
      await operationalExecutionAPI.updateReason(reasonId, payload);
      toast.success('Motivo actualizado.');
    } else {
      await operationalExecutionAPI.createReason(payload);
      toast.success('Motivo criado.');
    }
    await loadBootstrap(referenceDate);
  };

  const handleReviewCorrection = async (correctionId, payload) => {
    await operationalExecutionAPI.reviewCorrection(correctionId, payload);
    toast.success(payload.decision === 'approved' ? 'Correcção aprovada.' : 'Correcção rejeitada.');
    await loadBootstrap(referenceDate);
  };

  if (!canView) {
    return (
      <ModuleStateBoundary
        moduleName="Execução Operacional"
        loading={false}
        error={null}
        isEmpty
        emptyTitle="Acesso restrito"
        emptyDescription="Este módulo está disponível apenas para perfis autorizados."
        data-testid="operational-execution-access-boundary"
      />
    );
  }

  return (
    <ModuleStateBoundary
      moduleName="Execução Operacional"
      loading={loading}
      error={null}
      isEmpty={!moduleData && !loading}
      emptyTitle="Sem dados operacionais"
      emptyDescription="Ainda não foi possível compor o workspace operacional."
      data-testid="operational-execution-page-boundary"
    >
      <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden bg-slate-50 px-4 pb-4 pt-3 sm:px-6 lg:px-8" data-testid="operational-execution-page">
        <section className="shrink-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm" data-testid="operational-execution-hero-section">
          <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_332px]">
            <div className="space-y-3 border-b border-slate-200 bg-[linear-gradient(135deg,rgba(248,250,252,1),rgba(255,255,255,1))] px-5 py-4 xl:border-b-0 xl:border-r">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" data-testid="operational-execution-hero-badge">
                <ClipboardCheck className="h-4 w-4" /> Execução Operacional
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500" data-testid="operational-execution-page-breadcrumbs">
                  <span>ERP CG Business</span>
                  <span className="text-slate-300">/</span>
                  <span>Operações</span>
                  <span className="text-slate-300">/</span>
                  <span className="text-slate-900">Execução Operacional</span>
                </div>
                <h1 className="max-w-2xl text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl" data-testid="operational-execution-page-title">Execução operacional com backlog, auditoria e leitura executiva</h1>
                <p className="max-w-2xl text-base leading-6 text-slate-600" data-testid="operational-execution-page-subtitle">
                  O colaborador gere o próprio dia com rapidez; a gestão acompanha execução, pendências, justificações e correcções numa única vista compacta.
                </p>
              </div>
            </div>

            <Card className="rounded-none border-0 border-slate-200 bg-white shadow-none" data-testid="operational-execution-control-card">
              <CardHeader className="space-y-2 border-b border-slate-200 px-4 pb-3.5 pt-3.5">
                <CardTitle className="text-base font-semibold text-slate-900">Controlo operacional</CardTitle>
                <p className="text-sm text-slate-500">Atualize a janela de trabalho sem perder o contexto visual do módulo.</p>
              </CardHeader>
              <CardContent className="space-y-3 px-4 pb-4 pt-3.5">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500" htmlFor="operational-reference-date">Data de referência</label>
                  <input id="operational-reference-date" type="date" className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none transition-colors focus:border-slate-900" value={referenceDate || ''} onChange={(event) => setReferenceDate(event.target.value)} data-testid="operational-reference-date-input" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-800" onClick={() => loadBootstrap(referenceDate)} disabled={refreshing} data-testid="operational-apply-date-button">
                    {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Aplicar
                  </Button>
                  <Button className="h-10 rounded-xl border-slate-200 px-4 text-sm text-slate-900 hover:bg-slate-50" variant="outline" onClick={() => loadBootstrap(referenceDate)} data-testid="operational-refresh-button">Actualizar</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Tabs defaultValue={activeTabs[0]} className="flex min-h-0 flex-1 flex-col space-y-3" data-testid="operational-execution-tabs">
          <div className="shrink-0 rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="operational-execution-tabs-shell">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1.5 bg-transparent p-2" data-testid="operational-execution-tabs-list">
              <TabsTrigger value="overview" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-500 shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="operational-overview-tab-trigger"><LayoutGrid className="mr-2 h-4 w-4" /> Overview</TabsTrigger>
              <TabsTrigger value="workspace" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-500 shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="operational-workspace-tab-trigger"><ClipboardList className="mr-2 h-4 w-4" /> Meu dia</TabsTrigger>
              {managerMode ? <TabsTrigger value="admin" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-500 shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="operational-admin-tab-trigger"><Layers3 className="mr-2 h-4 w-4" /> Gestão</TabsTrigger> : null}
              {managerMode ? <TabsTrigger value="dashboard" className="h-9 rounded-full border border-slate-200 bg-white px-3.5 text-xs font-medium text-slate-500 shadow-sm data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="operational-dashboard-tab-trigger"><Gauge className="mr-2 h-4 w-4" /> Dashboard</TabsTrigger> : null}
            </TabsList>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1" data-testid="operational-execution-scroll-region">
            <TabsContent value="overview" className="mt-0" data-testid="operational-overview-tab-content">
              <OperationalOverviewTab
                workspace={moduleData?.my_workspace}
                overview={moduleData?.overview}
                dashboard={moduleData?.dashboard}
                catalogSummary={moduleData?.catalog_summary}
                workers={moduleData?.workers}
              />
            </TabsContent>

            <TabsContent value="workspace" className="mt-0" data-testid="operational-workspace-tab-content">
              <OperationalWorkerTab
                workspace={moduleData?.my_workspace}
                reasons={moduleData?.reasons}
                onComplete={handleComplete}
                onJustify={handleJustify}
                onComment={handleComment}
                onRequestCorrection={handleRequestCorrection}
              />
            </TabsContent>

            {managerMode ? (
              <TabsContent value="admin" className="mt-0" data-testid="operational-admin-tab-content">
                <OperationalAdminTab
                  overview={moduleData?.overview}
                  catalog={moduleData?.catalog}
                  catalogSummary={moduleData?.catalog_summary}
                  reasons={moduleData?.reasons}
                  workers={moduleData?.workers}
                  onGenerate={handleGenerate}
                  onSaveCatalog={handleSaveCatalog}
                  onSetCatalogStatus={handleSetCatalogStatus}
                  onDeleteCatalog={handleDeleteCatalog}
                  onSaveReason={handleSaveReason}
                  onReviewCorrection={handleReviewCorrection}
                  onRefresh={() => loadBootstrap(referenceDate)}
                />
              </TabsContent>
            ) : null}

            {managerMode ? (
              <TabsContent value="dashboard" className="mt-0" data-testid="operational-dashboard-tab-content">
                <OperationalDashboardTab dashboard={moduleData?.dashboard} catalogSummary={moduleData?.catalog_summary} />
              </TabsContent>
            ) : null}
          </div>
        </Tabs>
      </div>
    </ModuleStateBoundary>
  );
}